import React, { useCallback } from 'react';
import { twMerge } from 'tailwind-merge';
import useAppContext from 'flow/hooks/useAppContext';
import Icon from 'flow/components/ui-components/Icon';
import ContextMenu from 'flow/components/ContextMenu';
import MenuItem from 'flow/components/ui-components/MenuItem';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    muteParticipant,
    removeParticipant,
    turnOffParticipantVideo,
} from 'flow/actions/meeting-actions';
import {
    selectViewerCanRemoveParticipants,
    selectViewerIsInternal,
} from 'flow/selectors/meeting-selectors';
import MeetingContactAvatar from 'flow/components/meeting/shared/MeetingContactAvatar';
import { MeetingMember } from './useMeetingMembers';
import { getMoreOptionsLabel } from 'flow/utils/a11y-utils';

interface Props {
    member: MeetingMember;
    lazyLoad: boolean;
}

// Hoisted: a fresh element each render would defeat MeetingContactAvatar's memo.
const PHONE_GLYPH = (
    <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-700">
        <Icon iconKey={'phone'} />
    </div>
);

export const MeetingParticipantAvatarSlot = ({ member, lazyLoad }: Props) => (
    <MeetingContactAvatar
        chatUserKey={member.chatUserKey}
        phoneNumber={member.phoneNumber}
        displayName={member.displayName}
        size="toolbar"
        lazyLoad={lazyLoad}
        fallback={member.memberIsCaller ? PHONE_GLYPH : undefined}
        showStatus={true}
    />
);

/**
 * Muted-mic / camera-off status icons. Each keeps its own fixed-size slot
 * whether or not that track is actually off, so the icons — and the menu
 * trigger after them — line up in the same column across every row instead
 * of shifting as tracks toggle (AC-1834).
 */
export const MeetingParticipantStatusIcons = ({
    member,
}: {
    member: MeetingMember;
}) => (
    <>
        <span
            className={twMerge(
                'flex size-6 shrink-0 items-center justify-center',
                member.audioActive && 'invisible',
            )}
            data-test-id="participant-list-mic-off"
        >
            <Icon iconKey="mic_off" className="text-gray-400" />
        </span>
        <span
            className={twMerge(
                'flex size-6 shrink-0 items-center justify-center',
                // A caller has no camera at all, so the icon never shows for
                // that row — but the slot itself still needs to be there, or
                // caller rows are one slot short of every other row (AC-1834).
                (member.videoActive || member.memberIsCaller) && 'invisible',
            )}
            data-test-id="participant-list-camera-off"
        >
            <Icon iconKey="videocam_off" className="text-gray-400" />
        </span>
    </>
);

const MeetingParticipantName = ({ member }: { member: MeetingMember }) => {
    const { i18next } = useAppContext();

    const name =
        member.memberIsCaller && !member.displayName
            ? member.callerId
            : member.displayName;

    return (
        <span className="min-w-0 flex-1 truncate">
            {name}
            {member.memberIsSelf ? (
                <span className="text-gray-400">
                    {` (${i18next.t('chat.you')})`}
                </span>
            ) : null}
        </span>
    );
};

// Per-participant actions menu: mute/turn-off-camera (shown disabled once already off) and remove (gated per context — see selectViewerCanRemoveParticipants).
export const MeetingParticipantMenu = ({
    member,
}: {
    member: MeetingMember;
}) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const canRemove = useAppSelector(selectViewerCanRemoveParticipants);
    const viewerIsInternal = useAppSelector(selectViewerIsInternal);

    const mute = useCallback(
        () => dispatch(muteParticipant(member.name)),
        [dispatch, member.name],
    );
    const turnOffCamera = useCallback(
        () => dispatch(turnOffParticipantVideo(member.name)),
        [dispatch, member.name],
    );
    const remove = useCallback(
        () => dispatch(removeParticipant(member.name)),
        [dispatch, member.name],
    );
    // External guests get no host-control actions on other participants at
    // all — mute/camera-off included — not just a disabled/empty menu. The
    // trigger's slot stays reserved (AC-1834) so the status icons before it
    // don't shift between rows that do and don't have a menu.
    if (member.memberIsSelf || member.memberIsCaller || !viewerIsInternal) {
        return <div className="ms-auto size-8 shrink-0" />;
    }

    return (
        <ContextMenu
            triggerLabel={getMoreOptionsLabel(i18next, member.displayName)}
            className="ms-auto flex size-8 shrink-0 items-center justify-center !rounded-xl !bg-transparent !p-0 hover:!bg-gray-700"
            menuClassName="no-dark min-w-[180px] !bg-gray-700 text-white"
            placement="bottom-end"
            icon={<Icon iconKey="more_vert" className="text-white" />}
            dataTestId="participant-list-menu"
        >
            <MenuItem
                onClick={mute}
                disabled={!member.audioActive}
                closeOnClick={true}
                className={twMerge(
                    '!bg-gray-700',
                    member.audioActive
                        ? '!text-white hover:!bg-gray-600'
                        : '!text-gray-500',
                )}
                dataTestId="participant-list-mute"
            >
                <Icon
                    iconKey={member.audioActive ? 'mic_outlined' : 'mic_off'}
                    className="me-1.5"
                />
                <div>
                    {i18next.t(
                        member.audioActive
                            ? 'videoconference.host_controls.mute'
                            : 'videoconference.host_controls.muted',
                    )}
                </div>
            </MenuItem>
            <MenuItem
                onClick={turnOffCamera}
                disabled={!member.videoActive}
                closeOnClick={true}
                className={twMerge(
                    '!bg-gray-700',
                    member.videoActive
                        ? '!text-white hover:!bg-gray-600'
                        : '!text-gray-500',
                )}
                dataTestId="participant-list-turn-off-video"
            >
                <Icon
                    iconKey={member.videoActive ? 'video' : 'videocam_off'}
                    className="me-1.5"
                />
                <div>
                    {i18next.t(
                        member.videoActive
                            ? 'videoconference.host_controls.turn_off_camera'
                            : 'videoconference.host_controls.camera_off',
                    )}
                </div>
            </MenuItem>
            {canRemove && (
                <MenuItem
                    onClick={remove}
                    closeOnClick={true}
                    className="!bg-gray-700 !text-white hover:!bg-gray-600"
                    dataTestId="participant-list-remove"
                >
                    <Icon iconKey="not-interested" className="me-1.5" />
                    <div>
                        {i18next.t('videoconference.host_controls.remove')}
                    </div>
                </MenuItem>
            )}
        </ContextMenu>
    );
};

const MeetingParticipantRow = ({ member, lazyLoad }: Props) => (
    <div className="group flex items-center gap-x-3 rounded-sm px-6 py-2 text-base hover:bg-gray-700">
        <MeetingParticipantAvatarSlot member={member} lazyLoad={lazyLoad} />
        <MeetingParticipantName member={member} />
        <div className="flex shrink-0 items-center gap-1.5">
            <MeetingParticipantStatusIcons member={member} />
        </div>
        <MeetingParticipantMenu member={member} />
    </div>
);

// memo: the participant list re-renders whenever its ancestor MeetingVideoCanvas
// does; with useMeetingMembers handing out stable member objects, a row only
// re-renders when its own member actually changes.
export default React.memo(MeetingParticipantRow);
